/**
 * Deploy row and deploy list. Screen spec 4.10, and S8a's timeline.
 *
 * One row shape carries every list of deploys in the product: the S8 timeline,
 * S7's recent deploys, and the latest deploy line on an S6 card. Writing it
 * three times is how three screens end up disagreeing about what a deploy is.
 *
 * SCAN ORDER, and it is the reason the columns are in this order: a person
 * comes to this list asking "what happened and is it fine". So the row leads
 * with the state, then the human sentence that says what changed, then who and
 * when, and it ends with the hash, which is the one thing on the row nobody
 * reads unless they came here to read it.
 *
 * THE BRANCH AND THE HASH ARE ADVANCED. Both are identifiers, and the brief's
 * sorting test puts an identifier in Advanced. The projects list already ruled
 * the hash that way, so a row that printed it unconditionally made the same
 * fact Advanced on one screen and Simple on four. In Simple the row is state,
 * sentence, who, when. Advanced appends the branch to the meta line and the
 * hash to its own column, and the column itself is dropped in Simple rather
 * than left standing empty, so nothing moves and nothing is reserved for a
 * fact that is not there.
 *
 * ZERO RADIUS, deliberately. These rows are a ledger, not a stack of cards. A
 * radius on every row would put twenty rounded rectangles on a screen whose
 * whole argument is that the interface spends almost nothing on itself. The
 * hairline between rows is the only separator, and the rail is what makes them
 * read as one sequence rather than as twenty things.
 *
 * MOTION. A row that is still deploying carries a determinate bar, sized from
 * the clock, plus the name of the step it is on. The bar has no animation of
 * its own: it is a width that changes when the engine's clock changes, so the
 * only keyframed motion on a deploys screen stays the one rotating arc inside
 * the status lamp. A percentage without a step name is a spinner wearing a
 * number, which this product does not ship.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { StatusPill } from './StatusPill';
import { relativeTime, shortSha } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { getLang, t } from '../../lib/i18n/t';
import type { Deploy, DeployStatus, DeployTrigger, StatusValue } from '../../lib/types';
import '../../styles/components/deploy-row.css';

const WEEK = 7 * 24 * 60 * 60 * 1000;

function locale(): string {
  return getLang() === 'th' ? 'th-TH' : 'en-GB';
}

/**
 * An absolute date: "17 August". The year appears only when it is not this
 * one, because a year on every date is noise eleven months out of twelve.
 */
export function dateLabel(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const sameYear = at.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat(locale(), {
    day: 'numeric',
    month: 'long',
    year: sameYear ? undefined : 'numeric',
  }).format(at);
}

/**
 * Relative time under seven days, an absolute date beyond it, and never both.
 * Screen spec 6.5 rule 8. It lives here rather than in lib/format because this
 * is the rule about deploys specifically, and the two screens that also need
 * it are both describing a deploy.
 */
export function whenLabel(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return '';
  if (now - time >= WEEK) return dateLabel(iso, now);
  const rel = relativeTime(iso, now);
  return rel ? t(rel.key, { n: rel.n }) : '';
}

/**
 * `whenLabel`, but null for an unknown/absent timestamp instead of a rendered
 * word for `NaN` days. cloud-api genuinely has no source for some timestamps
 * (see database.ts's `updated_at: null`): "not shown" is the correct answer,
 * not a bug to paper over with a placeholder.
 */
export function whenLabelOrNull(iso: string, now = Date.now()): string | null {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return null;
  return whenLabel(iso, now);
}

/**
 * How long a build took, in words and in one key. Seconds up to a minute, then
 * minutes and a zero padded remainder, which is the rounding the fixture's own
 * durations are written in ("1m 06s").
 */
export function tookLabel(ms: number | null): string {
  if (ms === null) return '';
  const seconds = Math.round(ms / 1000);
  if (seconds === 1) return t('1 second');
  if (seconds < 60) return t('{n} seconds', { n: seconds });
  return t('{m}m {s}s', {
    m: Math.floor(seconds / 60),
    s: String(seconds % 60).padStart(2, '0'),
  });
}

/**
 * What started a deploy, in words, for the two screens that print it.
 *
 * A branch per case rather than a lookup table read through t(variable): the
 * extractor scans for literals, and a table of English strings passed through
 * t() as a variable renders in English forever because nothing ever put those
 * strings in the catalogue.
 */
export function triggerWord(trigger: DeployTrigger): string {
  if (trigger === 'manual') return t('You started it');
  if (trigger === 'rollback') return t('Putting an older version back');
  return t('A push to main');
}

/**
 * The pill has no queued lamp of its own: a queued deploy is work in flight,
 * and it reads as Deploying, which is the same neutral ring the person is
 * already waiting on. Inventing an eighth lamp to distinguish "about to start"
 * from "started" would spend a shape on a distinction nobody acts on.
 */
export function pillValue(status: DeployStatus): StatusValue {
  return status === 'queued' ? 'deploying' : status;
}

/** The rail dot repeats the lamp's tone. It is decoration: the pill has the word. */
const DOT_TONE: Record<DeployStatus, string> = {
  live: 'live',
  deploying: 'building',
  queued: 'building',
  'didnt-work': 'attention',
  replaced: 'idle',
  skipped: 'idle',
};

export interface DeployProgress {
  /** The step being worked on now. Never a bar without a name beside it. */
  stepLabel: string;
  /** 0 to 1, read from the timeline engine's clock. */
  fraction: number;
}

export interface DeployRowProps {
  deploy: Deploy;
  /** Where the whole row goes. A row with no target is not a link. */
  to?: string;
  /** Draws the rail and its dot. The timeline sets it, a card does not. */
  onRail?: boolean;
  /** Present only while the deploy is still running. */
  progress?: DeployProgress;
}

/**
 * The list the rows sit in. It owns the rail's start and end, so the rail
 * begins at the first dot and stops at the last one instead of dangling off
 * both ends of the history.
 */
export function DeployList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <ul className="dlist" aria-label={label}>
      {children}
    </ul>
  );
}

export function DeployRow({ deploy, to, onRail = false, progress }: DeployRowProps) {
  const running = progress !== undefined;
  const [advanced] = useAdvanced();

  const inner = (
    <>
      {onRail ? (
        <span className="drow__rail" aria-hidden="true">
          <span className="drow__dot" data-tone={DOT_TONE[deploy.status]} />
        </span>
      ) : null}

      <span className="drow__status">
        <StatusPill value={pillValue(deploy.status)} size="sm" live={running} />
      </span>

      <span className="drow__message" title={deploy.commitMessage}>
        {deploy.commitMessage}
      </span>

      <span className="drow__meta">
        <span className="drow__author">{deploy.author}</span>
        {advanced ? <span className="drow__branch mono">{deploy.branch}</span> : null}
        <span className="drow__when">
          {running ? progress.stepLabel : whenLabel(deploy.createdAt)}
        </span>
      </span>

      {advanced ? <span className="drow__sha mono">{shortSha(deploy.commitSha)}</span> : null}

      {running ? (
        <span className="drow__track" aria-hidden="true">
          <span
            className="drow__bar"
            style={{ inlineSize: `${Math.round(progress.fraction * 100)}%` }}
          />
        </span>
      ) : null}
    </>
  );

  return (
    <li className="dlist__item" data-rail={onRail ? 'yes' : 'no'}>
      {to ? (
        <Link
          className="drow"
          to={to}
          data-rail={onRail ? 'yes' : 'no'}
          data-idents={advanced ? 'yes' : 'no'}
        >
          {inner}
        </Link>
      ) : (
        <div
          className="drow"
          data-rail={onRail ? 'yes' : 'no'}
          data-idents={advanced ? 'yes' : 'no'}
        >
          {inner}
        </div>
      )}
    </li>
  );
}
